import React, { useState } from 'react'
import { CSVLink } from 'react-csv'
import DialogContent from '@material-ui/core/DialogContent'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import FileDownloadOutlinedIcon from 'components/material-symbol/icons/FileDownload'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import Link from '@material-ui/core/Link'
import RecordBookingPaymentDialog from 'components/record-booking-payment-dialog'
import AccountRouterLink from 'components/account-router-link'
import { formatPhoneNumber } from 'utils/phoneNumber'
import { useStyles } from './styles'
import { toFullDateAndTime } from 'utils/dateUtils'
import { centsToDollars } from 'lib/utils/number'
import CancelEventRegistrationDialog from 'components/cancel-event-registration-dialog'
import Tooltip from '@material-ui/core/Tooltip'
import IconButton from '@material-ui/core/IconButton'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import PaymentIcon from 'components/material-symbol/icons/Payment'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import MessageOutlined from 'components/material-symbol/icons/Message'
import {
  useRouteMatch,
  Link as LinkTo,
  Link as RouterLink,
} from 'react-router-dom'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import Waitlist from './waitlist'
import { useAuth } from 'lib/auth'
import {
  generateParticipantCsvData,
  getEventRegisterPath,
  getRsvpStatus,
} from './utils'
import AttendanceModal from '../attendance-modal'
import CollectPaymentDialog from './collect-payment-dialog'
import Badge from '@material-ui/core/Badge'
import { useIsMobile } from 'lib/hooks'
import SubviewPageHeader from '../subview-page-header'
import CopyTextButton from 'components/copy-text-button'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import PeopleAltOutlinedIcon from 'components/material-symbol/icons/PeopleAlt'
import LinkOutlinedIcon from 'components/material-symbol/icons/Link'
import { gtmEvent } from 'lib/gtm'

const ExportParticipantsButton = ({
  event,
  exportOptions,
  isHopeEvent,
  classes,
  onExport,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const hasMultipleOptions = exportOptions.length > 1

  const getCsvProps = option => ({
    data: generateParticipantCsvData(
      event.enrollments,
      event?.waitlist?.entries,
      isHopeEvent,
      option.value === 'waitlist',
      option.value === 'canceled attendees',
    ),
    filename: `${event.title}-${event.startDateTime}-${option.value}.csv`,
    onClick: onExport,
  })

  if (!hasMultipleOptions) {
    const option = exportOptions[0]
    return (
      <Button
        variant="outlined"
        color="primary"
        component={CSVLink}
        startIcon={<FileDownloadOutlinedIcon />}
        className={classes.exportButton}
        {...getCsvProps(option)}
      >
        Export
      </Button>
    )
  }

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<FileDownloadOutlinedIcon />}
        className={classes.exportButton}
        onClick={menuEvent => setAnchorEl(menuEvent.currentTarget)}
      >
        Export
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {exportOptions.map(option => (
          <MenuItem
            key={option.value}
            component={CSVLink}
            className={classes.exportMenuItem}
            onClick={() => {
              onExport()
              setAnchorEl(null)
            }}
            {...getCsvProps(option)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

const HopeLinkWrapper = ({ condition, wrapper, children }) => {
  return condition ? wrapper(children) : children
}

const BookStudentButton = ({
  event,
  className,
  children = 'Book a student',
}) => {
  const registerPath = getEventRegisterPath(event)

  if (!registerPath) {
    return null
  }

  return (
    <Button
      variant="outlined"
      color="primary"
      component={RouterLink}
      to={registerPath}
      className={className}
      onClick={() =>
        gtmEvent({
          event: 'formSubmit',
          formCategory: 'book-group-event',
          formAction: 'clicked-book-student-button',
        })
      }
    >
      {children}
    </Button>
  )
}

const AttendeesEmptyState = ({ event }) => {
  const classes = useStyles()
  const shareUrl = event.externalRegistrationUrl || event.eventUrl

  return (
    <Box className={classes.emptyState}>
      <CircularHighlightedIcon
        icon={PeopleAltOutlinedIcon}
        size={64}
        iconSize={28}
        highlightColor="#C5A561"
        iconColor="#FFFFFF"
      />
      <Typography variant="h6" className={classes.emptyStateTitle}>
        No attendees yet
      </Typography>
      <Typography variant="body2" className={classes.emptyStateCaption}>
        Share your registration link so students can sign up for this event.
      </Typography>
      <Box className={classes.emptyStateActions}>
        <CopyTextButton
          value={shareUrl}
          component={Button}
          variant="contained"
          className={classes.tonalButton}
          notificationText="Link copied to clipboard"
          startIcon={<LinkOutlinedIcon />}
          disabled={!shareUrl}
        >
          Share registration link
        </CopyTextButton>
        <BookStudentButton event={event} />
      </Box>
    </Box>
  )
}

export const PaymentDetails = ({
  paymentDate,
  registrationPaymentId,
  showLink = false,
}) => {
  const paymentText = `Paid on ${toFullDateAndTime(paymentDate)}`

  if (showLink) {
    return (
      <Link
        component={AccountRouterLink}
        to={`/account/payments/transactions/${registrationPaymentId}`}
        variant="caption"
      >
        {paymentText}
      </Link>
    )
  } else {
    return <Typography variant="caption">{paymentText}</Typography>
  }
}

const ParticipantsList = ({ event, enrollments, isHopeEvent = false }) => {
  const {
    title,
    startDateTime,
    priceInCents,
    acceptsOnlinePayments,
    slug,
  } = event
  const classes = useStyles()
  const { path } = useRouteMatch()
  const isMobile = useIsMobile()

  const { user } = useAuth()
  const isUserPrimaryCoach = user?.id === event.coach.id

  const URL = `${path
    .split('/')
    .slice(0, 2)
    .join('/')}/${slug}`
  return (
    <Box className={classes.studentContent} width="100%">
      {enrollments.map(enrollment => {
        const { student, createdAt, isCanceled } = enrollment
        const transactions = event.sessions[0].enrollments
          ? event.sessions[0].enrollments.find(e => e.student.id === student.id)
            ?.transactions
          : enrollments.find(e => e.student.id === student.id)?.transactions

        const productCount =
          transactions?.[0]?.registrationPayment?.products?.length || 1
        const studentInitials = `${student.firstName.substring(
          0,
          1,
        )}${student.lastName.substring(0, 1)}`
        const studentName = `${student.firstName} ${student.lastName}`
        const isPaid = transactions?.some(t => !t.refund)
        const paymentAmount =
          (isPaid &&
            centsToDollars(transactions?.[0]?.amountInCents / productCount)) ||
          0
        const paymentDate = isPaid && transactions?.[0]?.createdAt
        const registrationPaymentId =
          isPaid && transactions?.[0]?.registrationPayment?.id
        const contactInformation = [
          ...new Set(
            student.representatives
              .flatMap(r => [
                r.contact.email,
                formatPhoneNumber(r.contact.phone),
              ])
              .filter(s => s != null && s.trim().length > 0),
          ),
        ].join(' | ')
        const rsvpStatusIcon = getRsvpStatus(enrollment, event)
        return (
          <Box
            key={enrollment.id}
            display="flex"
            alignItems="flex-start"
            mb={2}
          >
            <HopeLinkWrapper
              condition={isHopeEvent}
              wrapper={children => (
                <LinkTo
                  to={`${URL}/${student.id}`}
                  style={{
                    textDecoration: 'none',
                    color: 'rgba(0, 0, 0, 0.87)',
                  }}
                >
                  {children}
                </LinkTo>
              )}
            >
              <Badge
                invisible={!rsvpStatusIcon}
                overlap="circular"
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                badgeContent={rsvpStatusIcon}
                className={classes.avatarWrapper}
              >
                <Avatar>{studentInitials}</Avatar>
              </Badge>
            </HopeLinkWrapper>
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              flexGrow={1}
            >
              <Box>
                <HopeLinkWrapper
                  condition={isHopeEvent}
                  wrapper={children => (
                    <LinkTo
                      to={`${URL}/${student.id}`}
                      style={{
                        textDecoration: 'none',
                        color: 'rgba(0, 0, 0, 0.87)',
                      }}
                    >
                      {children}
                    </LinkTo>
                  )}
                >
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    {studentName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {contactInformation}
                  </Typography>
                </HopeLinkWrapper>
                <Typography variant="caption" display="block">
                  Registered {toFullDateAndTime(createdAt)}
                  &nbsp;|&nbsp;
                  {isPaid ? (
                    <PaymentDetails
                      paymentAmount={paymentAmount}
                      paymentDate={paymentDate}
                      registrationPaymentId={registrationPaymentId}
                      showLink={isUserPrimaryCoach}
                    />
                  ) : (
                    <Typography variant="caption">Not Paid</Typography>
                  )}
                </Typography>
              </Box>
              <Box display="flex" flexDirection={isMobile ? 'column' : 'row'}>
                <Box textAlign={isMobile ? 'unset' : 'right'}>
                  {!isPaid && !isCanceled && (
                    <SensitiveInfoWrapper>
                      <CollectPaymentDialog
                        event={event}
                        enrollment={enrollment}
                      />
                      <RecordBookingPaymentDialog
                        trigger={
                          <Tooltip title="Mark as Paid">
                            <IconButton>
                              <PaymentIcon color="primary" />
                            </IconButton>
                          </Tooltip>
                        }
                        enrollment={enrollment}
                        eventTitle={title}
                        eventDate={startDateTime}
                        participantName={studentName}
                        defaultAmountInCents={priceInCents}
                        acceptOnlinePayments={acceptsOnlinePayments}
                      />
                    </SensitiveInfoWrapper>
                  )}
                  {!isCanceled && (
                    <>
                      <CancelEventRegistrationDialog
                        trigger={
                          <Tooltip title="Cancel Event Registration">
                            <IconButton>
                              <DeleteOutlinedIcon color="primary" />
                            </IconButton>
                          </Tooltip>
                        }
                        studentId={student.id}
                        eventId={event.id}
                        acceptsOnlinePayments={acceptsOnlinePayments}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

const EventParticipantsContent = ({ setIsOpen, event, embedded = false }) => {
  const { tags } = event
  const classes = useStyles({ embedded })

  const enrollmentSource = event.enrollments ?? []
  const canceledEnrollments = enrollmentSource.filter(e => e.isCanceled)
  const enrollments = enrollmentSource.filter(e => !e.isCanceled)
  const representatives = enrollments.flatMap(e => e.student.representatives)
  const contactExternalIds = representatives.map(rep => rep.contact.externalId)
  const isHopeEvent = tags.includes('PGA HOPE')
  const waitlistCount = event?.waitlist?.entries.length
  const showWaitlist = waitlistCount > 0
  const hasRegisteredAttendees = enrollments.length > 0

  const exportOptions = [{ value: 'attendees', label: 'Export Attendees' }]

  canceledEnrollments.length > 0 &&
    exportOptions.push({
      value: 'canceled attendees',
      label: 'Export Canceled Attendees',
    })

  showWaitlist &&
    exportOptions.push({
      value: 'waitlist',
      label: 'Export Waitlist',
    })

  return (
    <>
      <SubviewPageHeader
        eventTitle={event.title}
        pageTitle="Attendees"
        onBack={() => setIsOpen('detail')}
        actions={
          <>
            {hasRegisteredAttendees ? (
              <>
                <SensitiveInfoWrapper>
                  <StreamSendMessageButton
                    userIds={contactExternalIds}
                    component={IconButton}
                    color="primary"
                  >
                    <MessageOutlined />
                  </StreamSendMessageButton>
                </SensitiveInfoWrapper>
                <ExportParticipantsButton
                  event={event}
                  exportOptions={exportOptions}
                  isHopeEvent={isHopeEvent}
                  classes={classes}
                  onExport={() => setIsOpen('detail')}
                />
                <BookStudentButton
                  event={event}
                  className={classes.headerActionButton}
                />
              </>
            ) : null}
            {event?.collectAttendance &&
            event?.currentSession &&
            hasRegisteredAttendees ? (
              <AttendanceModal
                trigger={
                  <Button variant="contained" color="primary">
                    Attendance
                  </Button>
                }
                event={event}
              />
            ) : null}
          </>
        }
      />
      {hasRegisteredAttendees ? (
        <ParticipantsList
          event={event}
          enrollments={enrollments}
          isHopeEvent={isHopeEvent}
        />
      ) : (
        <AttendeesEmptyState event={event} />
      )}
      {canceledEnrollments.length > 0 && (
        <>
          <hr />
          <Typography variant="h6" className={classes.headerFont}>
            Canceled Attendees ({canceledEnrollments.length})
          </Typography>
          <ParticipantsList
            event={event}
            enrollments={canceledEnrollments}
            isHopeEvent={isHopeEvent}
          />
        </>
      )}
      {showWaitlist && (
        <>
          <hr />
          <Typography variant="h6" className={classes.headerFont}>
            Waitlist ({waitlistCount})
          </Typography>
          <Waitlist contacts={event.waitlist.entries} />
        </>
      )}
    </>
  )
}

const EventParticipants = ({ setIsOpen, event, embedded = false }) => {
  const classes = useStyles({ embedded })

  if (embedded) {
    return (
      <Box className={classes.embeddedRoot}>
        <EventParticipantsContent {...{ setIsOpen, event, embedded }} />
      </Box>
    )
  }

  return (
    <DialogContent>
      <EventParticipantsContent {...{ setIsOpen, event, embedded }} />
    </DialogContent>
  )
}

export default EventParticipants
